import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { useDatabase } from "./DatabaseContext";
import {
  createUser,
  decryptUserRecord,
  encryptUser,
  User,
  UserRecord,
} from "../models/user";

interface LoginContextData {
  isLoggedIn: boolean;
  user: User | undefined;
  cypherKey: Uint8Array | undefined;
  login: (data: { userId: string; password: string }) => Promise<boolean>;
  createNewUser: (data: {
    userId: string;
    password: string;
    name: string;
  }) => Promise<boolean>;
  setNewPassword: (password: string) => Promise<boolean>;
  logout: () => void;
}

const notImplemented = () => {
  throw new Error("Not implemented");
};

const LoginContext = createContext<LoginContextData>({
  isLoggedIn: false,
  user: undefined,
  cypherKey: undefined,
  login: notImplemented,
  logout: notImplemented,
  createNewUser: notImplemented,
  setNewPassword: notImplemented,
});

export const LoginContextProvider: React.FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const db = useDatabase();
  const [{ user }, setState] = useState<{ user?: User }>({});
  const ctxValue = useMemo<LoginContextData>(() => {
    return {
      isLoggedIn: user !== undefined,

      user,

      cypherKey: user && Uint8Array.from(user.cypherKey),

      login: async ({ userId, password }) => {
        const collection = db.readCollection<UserRecord>("users");
        const myData = await collection.get(userId);
        if (!myData) return false;

        try {
          const user = await decryptUserRecord(myData, password);

          setState({ user });
          return true;
        } catch (e) {
          console.error(e);
          return false;
        }
      },

      logout: () => setState({}),

      createNewUser: async ({ userId, name, password }) => {
        //check if user exists already
        const collection = db.readWriteCollection<UserRecord>("users");
        const currentUser = await collection.get(userId);
        if (currentUser !== undefined) {
          return false;
        }
        const user = await createUser({ userId, name });
        const userRecord = await encryptUser(user, password);

        //store user in DB
        await collection.set(userId, userRecord);

        //finish
        setState({ user });
        return true;
      },

      setNewPassword: async (password: string) => {
        if (!user) return false;

        const collection = db.readWriteCollection<UserRecord>("users");
        const userRecord = await encryptUser(user, password);

        //store user in DB
        await collection.set(user.userId, userRecord);

        //finish
        setState({ user });
        return true;
      },
    };
  }, [user, db]);

  return (
    <LoginContext.Provider value={ctxValue}>{children}</LoginContext.Provider>
  );
};

export const useIsLoggedIn = () => {
  const { isLoggedIn } = useContext(LoginContext);
  return isLoggedIn;
};

export const useLoginContext = () => useContext(LoginContext);
